"use strict";

import {
    Button,
    CircularProgress,
    Grid,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { GET_USERS } from "../ApiEndpoints";
import { Store } from "../Store";
import { fetchManagedTeams } from "../utils/teamRequests";

const TeamListScreen = () => {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userNameById, setUserNameById] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(
        searchParams.get("search") || ""
    );
    const tableCellSx = { py: 1 };

    const fetchTeams = useCallback(async () => {
        // Exit early when the requester context is not available yet.
        if (!userInfo?.id) {
            setTeams([]);
            setLoading(false);
            return;
        }

        try {
            // Request the teams list for the current admin.
            setLoading(true);
            const teamsData = await fetchManagedTeams({ requesterUserId: userInfo.id });

            // Normalize and store the teams response.
            setTeams(teamsData);
            setError(null);
        } catch (err) {
            console.error("Error fetching teams:", err);
            setTeams([]);
            setError("Failed to load teams");
        } finally {
            setLoading(false);
        }
    }, [userInfo?.id]);

    const fetchUsers = useCallback(async () => {
        try {
            // Request a larger page of users to map IDs to display names.
            const response = await axios.post(GET_USERS, {
                page: 1,
                search: "",
                pageSize: 1000,
            });

            // Build a lookup map for quick access in the table.
            const usersData = Array.isArray(response.data?.users)
                ? response.data.users
                : [];
            const lookup = {};
            usersData.forEach((user) => {
                if (user?.id) {
                    lookup[user.id] = user.name || user.email || user.id;
                }
            });
            setUserNameById(lookup);
        } catch (err) {
            console.error("Error fetching users for team mapping:", err);
            setUserNameById({});
        }
    }, []);

    useEffect(() => {
        // Fetch users once so we can display project manager names.
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        // Fetch teams whenever the requester context changes.
        fetchTeams();
    }, [fetchTeams]);

    useEffect(() => {
        const paramSearch = searchParams.get("search") || "";
        if (paramSearch !== searchTerm) {
            setSearchTerm(paramSearch);
        }
    }, [searchParams, searchTerm]);

    const teamRows = useMemo(() => {
        // Convert raw team data into display-friendly rows.
        return teams.map((team) => {
            const projectManagers = Array.isArray(team?.project_manager)
                ? team.project_manager
                : team?.project_manager
                    ? [team.project_manager]
                    : [];
            const projectManagerNames = projectManagers.map(
                (managerId) => userNameById[managerId] || managerId
            );
            const members = Array.isArray(team?.members) ? team.members : [];
            const teamId = team.team_id || team.id || team.team_name;

            return {
                id: teamId,
                name: team.team_name || "Untitled Team",
                projectManagerNames,
                projectManagerCount: projectManagers.length,
                memberCount: members.length,
                editPath: `/admin/teams/${encodeURIComponent(String(teamId))}/edit`,
            };
        });
    }, [teams, userNameById]);

    const filteredTeamRows = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) {
            return teamRows;
        }
        return teamRows.filter((team) =>
            (team.name || "").toLowerCase().includes(normalizedSearch)
        );
    }, [searchTerm, teamRows]);

    const handleSearchChange = (event) => {
        const nextValue = event.target.value;
        setSearchTerm(nextValue);
        if (nextValue.trim()) {
            setSearchParams({ search: nextValue });
        } else {
            setSearchParams({});
        }
    };

    if (loading) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: "50vh" }}
            >
                <CircularProgress />
            </Grid>
        );
    }

    if (error) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: "50vh" }}
            >
                <div>{error}</div>
            </Grid>
        );
    }

    return (
        <Grid item container justifyContent="center">
            <Grid item container xs={7} marginTop={7} direction="column">
                <Grid
                    item
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Grid
                        item
                        sx={{
                            fontSize: "var(--font-size-header)",
                            fontWeight: "600",
                        }}
                    >
                        Teams
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to="/admin/teams/new"
                        >
                            New Team
                        </Button>
                    </Grid>
                </Grid>
                <Grid item container alignItems="center" mb={3}>
                    <TextField
                        label="Search teams"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ maxWidth: 320 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item>
                    <Table size="small" sx={{ minWidth: 650, bgcolor: "white" }}>
                        <TableHead bgcolor="#5570dc">
                            <TableRow>
                                <TableCell sx={tableCellSx}>TEAM NAME</TableCell>
                                <TableCell sx={tableCellSx}>PROJECT MANAGER(S)</TableCell>
                                <TableCell sx={tableCellSx}>USERS (PM / NON-PM)</TableCell>
                                <TableCell sx={tableCellSx}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTeamRows.length > 0 ? (
                                filteredTeamRows.map((team) => {
                                    const pmLabel = `${team.projectManagerCount} PM`;
                                    const memberLabel = `${team.memberCount} non-PM`;
                                    const managersLabel =
                                        team.projectManagerNames.length > 0
                                            ? team.projectManagerNames.join(", ")
                                            : "Unassigned";

                                    return (
                                        <TableRow key={team.id}>
                                            <TableCell sx={tableCellSx}>{team.name}</TableCell>
                                            <TableCell sx={tableCellSx}>{managersLabel}</TableCell>
                                            <TableCell sx={tableCellSx}>
                                                {pmLabel} / {memberLabel}
                                            </TableCell>
                                            <TableCell sx={tableCellSx}>
                                                <Button
                                                    size="small"
                                                    component={RouterLink}
                                                    to={team.editPath}
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={tableCellSx}>
                                        No teams found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Grid>
                <Grid item mt={3}>
                    <Typography variant="body2">
                        Showing {filteredTeamRows.length} of {teamRows.length} teams
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TeamListScreen;
